import { RootState } from '..';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SalesforceItem } from '../../components/parser/ParserTypes';
import { BaseElementProperties } from '@server/documentation';
import { createConfigurationItemFromParserResponseItem } from '../../components/parser/parserUtils';

export interface MetadataTypeItems {
  [metadataType: string]: SalesforceItem[];
}

export interface MetadataIndexedLookup {
  [metadataType: string]: {
    [itemId: string]: SalesforceItem;
  };
}

export interface MetadataState {
  metadataTypesList: {
    [crmOrgId: string]: MetadataTypeItems;
  };
  metadataIndexedLookup: {
    [crmOrgId: string]: MetadataIndexedLookup;
  };
  chosenMetadataElements?: SalesforceItem[]; //present only when dependencies dialog is open
}

const initialState = {
  metadataTypesList: {} as { [crmOrgId: string]: MetadataTypeItems },
  metadataIndexedLookup: {} as { [crmOrgId: string]: MetadataIndexedLookup },
  chosenMetadataElements: undefined as SalesforceItem[] | undefined,
} satisfies MetadataState;

export const metadataSlice = createSlice({
  name: 'metadata',
  initialState,
  reducers: {
    setMetadataTypesList: (
      state,
      action: PayloadAction<{
        metadataTypesList: BaseElementProperties[];
        type: string;
        crmOrgId: string;
      }>,
    ) => {
      const { metadataTypesList, type, crmOrgId } = action.payload;
      const _metadataTypesList: MetadataTypeItems = {};
      const _metadataIndexedLookup: MetadataIndexedLookup = {};

      if (!metadataTypesList.length) {
        _metadataTypesList[type] = [];
        _metadataIndexedLookup[type] = {};
      } else {
        metadataTypesList.forEach((item) => {
          const newItem = createConfigurationItemFromParserResponseItem({
            responseItem: item,
            objectApiName: item.objectName ?? '',
            keyType: type,
          });

          if (!_metadataTypesList[type]) {
            _metadataTypesList[type] = [];
          }
          _metadataTypesList[type].push(newItem);

          // Build indexed lookup structure (performance optimization)
          if (!_metadataIndexedLookup[type]) {
            _metadataIndexedLookup[type] = {};
          }
          _metadataIndexedLookup[type][newItem.id] = newItem;
        });
      }

      // Update array-based structure
      state.metadataTypesList[crmOrgId] = {
        ...state.metadataTypesList[crmOrgId],
        ..._metadataTypesList,
      };

      // Update indexed lookup structure
      state.metadataIndexedLookup[crmOrgId] = {
        ...state.metadataIndexedLookup[crmOrgId],
        ..._metadataIndexedLookup,
      };
    },
    setChosenMetadataElement: (
      state,
      action: PayloadAction<{ id: string; type: string; crmOrgId: string }>,
    ) => {
      const { id, type, crmOrgId } = action.payload;

      const chosenMetadataElement = state.metadataIndexedLookup[crmOrgId]?.[type]?.[id];
      const fallbackElement =
        chosenMetadataElement ||
        state.metadataTypesList[crmOrgId]?.[type]?.find((element) => element.id === id);

      if (fallbackElement) {
        if (!state.chosenMetadataElements) {
          state.chosenMetadataElements = [];
        }
        state.chosenMetadataElements.push(fallbackElement);
      }
    },
    clearChosenMetadataElement: (state) => {
      state.chosenMetadataElements = undefined;
    },
  },
});

export const { setMetadataTypesList, setChosenMetadataElement, clearChosenMetadataElement } =
  metadataSlice.actions;

export const selectMetadataTypesList = (crmOrgId: string) => (state: RootState) =>
  state.metadata.metadataTypesList[crmOrgId];

export const selectChosenMetadataElement = (state: RootState) =>
  state.metadata.chosenMetadataElements;

export default metadataSlice.reducer;
